import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_CURRENCY,
  paymentMethods,
  qualifiesForFreeShipping,
} from "../../utils/global_const";
import InvoiceProduct from "./InvoiceProduct";
import styled from "styled-components";
import { ProductContext } from "../../context/ProductContext";
import { AuthUserContext } from "../../context/AuthUserContext";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";
import { useTranslation } from "react-i18next";
import { cartActions } from "../../store/cart-slice";
import { Card, CardTitle, Divider, PrimaryButton } from "./CheckoutUI";
import { getProductTitle } from "../../utils/productDisplayName";

const ProductList = styled.div`
  width: 100%;
  max-height: 320px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--bg-300);
    border-radius: var(--border-radius-small);
  }
`;

const Notice = styled.p`
  margin: var(--spacing-md) 0;
  font-size: var(--font-size-small);
  font-style: italic;
  color: var(--text-200);
  text-align: center;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: 6px 0;
`;

const SummaryLabel = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const SummaryValue = styled.span`
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--text-100);
`;

const TotalRow = styled(SummaryRow)`
  padding-top: var(--spacing-sm);
  margin-top: 4px;
  border-top: 1px solid var(--bg-300);
`;

const TotalLabel = styled.span`
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--text-100);
`;

const TotalValue = styled.span`
  font-size: var(--font-size-large);
  font-weight: 700;
  color: var(--text-100);
`;

const TermsRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-md);
  cursor: pointer;
`;

const TermsText = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-100);
`;

const SubmitButton = styled(PrimaryButton)`
  margin-top: var(--spacing-md);

  @media (min-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const Invoice = ({ address, setSubmit, paymentMethod, shippingMethod }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userProfile, authTokens } = useContext(AuthUserContext);
  const { currencyTag, createOrder, setCreatedOrder } =
    useContext(ProductContext);
  const [checkbox, setCheckbox] = useState(false);
  const navigate = useNavigate();
  const { goToThankYou } = useNavigation();

  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.discount_price * item.quantity,
    0
  );
  const subtotalRounded = parseFloat(subtotal.toFixed(2));
  const savings = cartItems.reduce(
    (totalSavings, item) =>
      totalSavings +
      (item.product.price - item.product.discount_price) * item.quantity,
    0
  );
  const savingsRounded = parseFloat(savings.toFixed(2));
  const isShippingValid =
    shippingMethod && Object.keys(shippingMethod).length > 0;

  const freeShippingApplies = qualifiesForFreeShipping(
    subtotalRounded,
    shippingMethod?.name
  );

  const shippingCost =
    isShippingValid && !freeShippingApplies ? shippingMethod.price : 0;

  const grandTotal = subtotal + shippingCost;
  const grandTotalRounded = grandTotal.toFixed(2);

  const handleSubmit = async () => {
    if (userProfile && !userProfile.is_email_verified && !checkbox) {
      return;
    }
    setSubmit(true);
    // Kreiranje objekta sa podacima narudžbine
    const orderData = {
      address: address.id,
      order_items: cartItems.map((item) => ({
        product: item.product.id,
        quantity: item.quantity,
      })),
      payment_method: paymentMethod,
      transport_method: shippingMethod.name,
      note: "",
    };
    try {
      // Poziv funkcije za kreiranje narudžbine (pretpostavljamo da funkcija `createAddressBook` postoji)
      const createdOrderCopy = await createOrder(orderData, authTokens.access);
      console.log("Order created successfully", createdOrderCopy);
      setCreatedOrder(createdOrderCopy);
      // Ovde dodajte logiku koja se izvršava nakon uspešnog kreiranja narudžbine
      const totalPrice = cartItems.reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      );
      const currency = localStorage.getItem("currency") || DEFAULT_CURRENCY;

      const items = cartItems.map((item, index) => ({
        item_id: item.product.id,
        item_name: getProductTitle(item.product.category_name, item.product.name),
        affiliation: "Snusco-AT", // Customize as needed
        discount:
          Number(item.product.price) - Number(item.product.discount_price),
        index: index,
        item_category: item.product.category_name,
        item_variant: item.product.nicotine + " MG",
        price: item.product.discount_price,
        quantity: item.quantity,
      }));
      const user_info = jwtDecode(authTokens.access);

      // 1) Isprazni korpu u Redux‑u i localStorage
      dispatch(cartActions.resetCart());

      navigate(goToThankYou());
    } catch (error) {
      console.error("Greška prilikom kreiranja narudžbine:", error); // Obradite grešku
    }
    setSubmit(false);
  };

  const inStockItems = cartItems.filter(
    (item) => item.product.is_in_stock === "in_stock"
  );
  const otherItems = cartItems.filter(
    (item) => item.product.is_in_stock !== "in_stock"
  );

  return (
    <Card>
      <CardTitle>{t("INVOICE.SUMMARY")}</CardTitle>

      <ProductList>
        {inStockItems.map((item, index) => (
          <InvoiceProduct item={item} key={index} />
        ))}
        {otherItems.length > 0 && (
          <>
            <Notice>{t("CART.NOTICE")}</Notice>
            {otherItems.map((item, idx) => (
              <InvoiceProduct item={item} key={`out-${idx}`} />
            ))}
          </>
        )}
      </ProductList>

      <Divider />

      <SummaryRow>
        <SummaryLabel>{t("INVOICE.SUBTOTAL")}</SummaryLabel>
        <SummaryValue>
          {currencyTag}
          {subtotalRounded}
        </SummaryValue>
      </SummaryRow>
      <SummaryRow>
        {shippingMethod?.name ? (
          <>
            <SummaryLabel>
              {t("INVOICE.SHIPPING")} - {shippingMethod.name}
            </SummaryLabel>
            <SummaryValue>
              {freeShippingApplies
                ? `🎉 ${t("CART.FREE_SHIPPING_UNLOCKED")}`
                : `${currencyTag}${shippingCost.toFixed(2)}`}
            </SummaryValue>
          </>
        ) : (
          <>
            <SummaryLabel>{t("INVOICE.SHIPPING")}</SummaryLabel>
            <SummaryValue>{t("INVOICE.SHIPPING_TBD")}</SummaryValue>
          </>
        )}
      </SummaryRow>
      <SummaryRow>
        <SummaryLabel>{t("CART.VAT")}</SummaryLabel>
        <SummaryValue>{t("CART.INCLUDED")}</SummaryValue>
      </SummaryRow>
      <TotalRow>
        <TotalLabel>{t("INVOICE.TOTAL_AMOUNT")}</TotalLabel>
        <TotalValue>
          {currencyTag}
          {grandTotalRounded}
        </TotalValue>
      </TotalRow>

      {address && paymentMethod && (
        <>
          <TermsRow onClick={() => setCheckbox(!checkbox)}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
                stroke="var(--text-200)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="var(--bg-100)"
              />
              {checkbox && (
                <path
                  d="M7.75 12L10.58 14.83L16.25 9.17004"
                  stroke="var(--primary-100)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
            <TermsText>{t("INVOICE.AGREE_TERMS")}</TermsText>
          </TermsRow>
          {paymentMethod === paymentMethods[0].code && (
            <SubmitButton onClick={handleSubmit} disabled={!checkbox}>
              {t("INVOICE.DELIVER")}
            </SubmitButton>
          )}
        </>
      )}
    </Card>
  );
};

export default Invoice;
